import type { HTMLAttributes } from "react";
import { cx } from "../../lib/cx";
import styles from "./Avatar.module.css";

export type AvatarSize = "sm" | "md" | "lg";

export type AvatarProps = {
  name: string;
  src?: string | null;
  size?: AvatarSize;
} & Omit<HTMLAttributes<HTMLSpanElement>, "children">;

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  const first = parts[0];
  if (!first) return "?";
  if (parts.length === 1) return first.charAt(0).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
};

// Deterministic color hash — same name yields same color
const seedColor = (name: string): string => {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  }
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 55%, 85%)`;
};

export const Avatar = ({ name, src, size = "md", className, style, ...rest }: AvatarProps) => (
  <span
    aria-label={name}
    data-size={size}
    className={cx(styles.avatar, className)}
    style={src ? style : { background: seedColor(name), ...style }}
    {...rest}
  >
    {src ? <img src={src} alt="" className={styles.img} /> : initials(name)}
  </span>
);
